import axios from "axios";
import classNames from "classnames";
import { useFormik } from "formik";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import { FC, Fragment } from "react";
import Button from "~/components/Button";
import { Container } from "~/components/Container";
import Input from "~/components/Input";
import Radio from "~/components/Radio";
import Text from "~/components/Text";
import Textarea from "~/components/Textarea";
import Colors from "~/constants/colors";
import { isValidEmail } from "~/helpers/form.helpers";
import Trans from "~/types/translationKeys";
import styles from "./Form.module.scss";

const serviceRadios = [
  Trans.ai_agent_deployment_and_customization,
  Trans.ai_application_development,
  Trans.ai_consultation_services,
  Trans.other_inquiry,
];

const Form: FC = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const {
    touched,
    values,
    errors,
    getFieldProps,
    submitForm,
    isSubmitting,
    setFieldValue,
    dirty,
  } = useFormik({
    enableReinitialize: true,
    validateOnBlur: false,
    initialValues: {
      email: "",
      message: "",
      service: Trans.other_inquiry,
    },
    validate: (values) => {
      const err: Record<string, string> = {};
      if (!values.email.trim()) {
        err.email = t(Trans.please_enter_your, {
          name: t(Trans.email).toLowerCase(),
        });
      } else if (!isValidEmail(values.email)) {
        err.email = t(Trans.wrong_email_format);
      }
      return err;
    },
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("Email", values.email);
      formData.append("Service", t(values.service));
      formData.append("Message", values.message);
      formData.append("Sheet", "AI Studio");
      const res = await axios.post(
        `${process.env.NEXT_PUBLIC_SCRIPT_URL}`,
        formData
      );
      if (res.data.row) {
        router.push("/ai-studio/thank-you");
      }
    },
  });

  return (
    <section className={styles.form} id="form">
      <div className={styles.pattern}>
        <Image src="/images/form_pattern.png" fill alt="pattern" />
      </div>

      <Container>
        <div className={styles.container}>
          <div className={styles.left}>
            <h2 className={styles.title}>
              {t(Trans.transform_your_business_with_cx_genie_s_ai_studio)}
            </h2>
            <p className={styles.content}>
              {t(
                Trans.lets_engineer_ai_solutions_together_that_drive_your_business_success_to_new_heights
              )}
            </p>
            <Image
              src="/images/form.png"
              alt="form"
              width={310}
              height={272}
              className="mx-auto"
            />
          </div>
          <div className={classNames(styles.right)}>
            <h2 className={styles.title}>{t(Trans.lets_chat)}</h2>
            <Input
              label={t(Trans.your, {
                name: t(Trans.email).toLowerCase(),
              })}
              required
              containerClassName="w-full"
              className="!h-10 !rounded-lg"
              placeholder={t(Trans.type, {
                name: t(Trans.email_address).toLowerCase(),
              })}
              {...getFieldProps("email")}
              error={touched.email && errors.email}
            />
            <div className="flex flex-col gap-2">
              <Text type="body-2" color={Colors.GREY_700}>
                {t(Trans.select_service)}
              </Text>
              {serviceRadios.map((service) => (
                <Radio
                  key={service}
                  label={t(service)}
                  checked={service === values.service}
                  onClick={() => setFieldValue("service", service)}
                />
              ))}
            </div>
            <Textarea
              label={t(Trans.your, {
                name: t(Trans.message),
              })}
              placeholder={t(Trans.type, {
                name: t(Trans.message_here),
              })}
              {...getFieldProps("message")}
            />
            <Button
              className="w-fit"
              disabled={!dirty}
              loading={isSubmitting}
              onClick={submitForm}
            >
              {t(Trans.submit)}
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Form;
